import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys, useApiMutation, useApiQuery, type ApiClient } from '@zagru/platform';
import type { CountryAddressSchema, PlaceFormatContext, PlaceOption, PlaceSource } from '@zagru/design-system';
import type { QueryOpts } from './common';
import { awaitUnlessAborted } from './refdata';

/*
 * Universal Location (D-021, ADR-0041): `/api/v1/geo`.
 *
 * Every location picker reads through `createPlaceSource` — a small stale-while-revalidate cache per tenant and
 * language that also remembers every place it has seen, so a stored place id renders by name without a request of
 * its own. Display formats resolve user › role › party › tenant › platform on the server (`/geo/display-formats`),
 * with the viewer's own choice from Preferences on top (D-032).
 */

export const geoCrumbSchema = z.object({ id: z.string(), type: z.string(), name: z.string(), code: z.string().nullable() });
export const geoPlaceSchema = z.object({
  id: z.string(),
  type: z.string(),
  name: z.string(),
  names: z.record(z.string(), z.string()),
  code: z.string().nullable(),
  countryCode: z.string().nullable(),
  parentId: z.string().nullable(),
  label: z.string(),
  hierarchy: z.array(geoCrumbSchema),
  levelName: z.string().nullable(),
  aliases: z.array(z.string()),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  source: z.string(),
  hasChildren: z.boolean(),
});
export type GeoPlace = z.infer<typeof geoPlaceSchema>;

export const geoSearchSchema = z.object({ items: z.array(geoPlaceSchema), recent: z.boolean() });
export const geoResolveSchema = z.object({ place: geoPlaceSchema.nullable(), chain: z.array(geoPlaceSchema), unmatched: z.array(z.string()), confidence: z.number() });
export const geoCountrySchemaSchema = z.object({
  countryCode: z.string(),
  levels: z.array(z.object({ type: z.string(), name: z.record(z.string(), z.string()) }).passthrough()),
  required: z.array(z.string()),
  postalPattern: z.string().nullable(),
  templates: z.record(z.string(), z.string()),
});
export type GeoCountrySchema = z.infer<typeof geoCountrySchemaSchema>;
export const geoDisplayFormatSchema = z.object({ context: z.string(), template: z.string(), level: z.string(), scopeId: z.string().nullable(), isDefault: z.boolean() });
export type GeoDisplayFormat = z.infer<typeof geoDisplayFormatSchema>;

export interface GeoTenantPlaceCommand {
  parentId?: string | null;
  type?: string | null;
  names?: Record<string, string> | null;
  aliases?: string[] | null;
  code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  active?: boolean | null;
}

const base = '/api/v1/geo';

export const geoKeys = {
  all: () => queryKeys.custom('geo'),
  place: (id: string, lang?: string) => queryKeys.custom('geo', 'place', id, lang ?? null),
  children: (id: string, lang?: string) => queryKeys.custom('geo', 'children', id, lang ?? null),
  search: (params: Record<string, unknown>) => queryKeys.custom('geo', 'search', params),
  schemas: () => queryKeys.custom('geo', 'country-schemas'),
  formats: (params: Record<string, unknown>) => queryKeys.custom('geo', 'display-formats', params),
};

export const toPlaceOption = (p: GeoPlace): PlaceOption => ({
  id: p.id,
  type: p.type,
  name: p.name,
  code: p.code,
  countryCode: p.countryCode,
  hierarchy: p.hierarchy,
  label: p.label,
  levelName: p.levelName,
  hasChildren: p.hasChildren,
  source: p.source,
});

export function usePlaceById(id: string | undefined, lang?: string, options?: QueryOpts<GeoPlace>) {
  return useApiQuery<GeoPlace>(geoKeys.place(id ?? '', lang), (client, signal) => client.get(`${base}/places/${encodeURIComponent(id ?? '')}`, { query: { lang }, schema: geoPlaceSchema, signal }), {
    enabled: !!id,
    staleTime: 5 * 60_000,
    ...(options ?? {}),
  });
}

export function usePlaceChildren(id: string | undefined, lang?: string, options?: QueryOpts<GeoPlace[]>) {
  return useApiQuery<GeoPlace[]>(
    geoKeys.children(id ?? '', lang),
    (client, signal) => client.get(`${base}/places/${encodeURIComponent(id ?? '')}/children`, { query: { lang, limit: 500 }, schema: z.array(geoPlaceSchema), signal }),
    { enabled: !!id, staleTime: 5 * 60_000, ...(options ?? {}) },
  );
}

export function useCountrySchemas(options?: QueryOpts<GeoCountrySchema[]>) {
  return useApiQuery<GeoCountrySchema[]>(geoKeys.schemas(), (client, signal) => client.get(`${base}/country-schemas`, { schema: z.array(geoCountrySchemaSchema), signal }), {
    staleTime: 60 * 60_000,
    ...(options ?? {}),
  });
}

export function useDisplayFormats(params: { partyId?: string; roleCodes?: string[]; userId?: string } = {}, options?: QueryOpts<GeoDisplayFormat[]>) {
  return useApiQuery<GeoDisplayFormat[]>(
    geoKeys.formats(params),
    (client, signal) =>
      client.get(`${base}/display-formats`, { query: { partyId: params.partyId, roleCodes: params.roleCodes?.join(','), userId: params.userId }, schema: z.array(geoDisplayFormatSchema), signal }),
    { staleTime: 5 * 60_000, ...(options ?? {}) },
  );
}

function useGeoInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    invalidatePlaceSources();
    void queryClient.invalidateQueries({ queryKey: geoKeys.all() });
  };
}

export function useCreateTenantPlace() {
  const invalidate = useGeoInvalidation();
  return useApiMutation<GeoPlace, GeoTenantPlaceCommand>((client, body) => client.post(`${base}/places`, body, { schema: geoPlaceSchema, idempotencyKey: true }), { onSuccess: invalidate });
}

export function useUpdateTenantPlace() {
  const invalidate = useGeoInvalidation();
  return useApiMutation<GeoPlace, { id: string; body: GeoTenantPlaceCommand }>((client, { id, body }) => client.patch(`${base}/places/${encodeURIComponent(id)}`, body, { schema: geoPlaceSchema }), {
    onSuccess: invalidate,
  });
}

export function useDeleteTenantPlace() {
  const invalidate = useGeoInvalidation();
  return useApiMutation<void, { id: string }>((client, { id }) => client.delete(`${base}/places/${encodeURIComponent(id)}`), { onSuccess: invalidate });
}

/* ------------------------------------------------------------------ the pickers' source */

const FRESH_MS = 60_000;
const liveSources = new Set<{ invalidate: () => void }>();

export function invalidatePlaceSources(): void {
  for (const source of liveSources) source.invalidate();
}

/** The `PlaceSource` behind every `PlacePicker` / `PlaceDisplay` (stale-while-revalidate per tenant and language). */
export function createPlaceSource(client: ApiClient, scope: { tenantId?: string | null; lang: string }): PlaceSource & { invalidate: () => void; dispose: () => void } {
  const searches = new Map<string, { at: number; items: PlaceOption[] }>();
  const places = new Map<string, PlaceOption | null>();
  const pending = new Map<string, Promise<PlaceOption | null>>();
  const remember = (list: PlaceOption[]) => {
    for (const p of list) places.set(p.id, p);
    return list;
  };

  const source = {
    async search(query: string, options: { types?: string[]; country?: string; within?: string; limit?: number; signal?: AbortSignal } = {}) {
      const key = JSON.stringify([scope.tenantId, scope.lang, query.trim().toLowerCase(), options.types ?? null, options.country ?? null, options.within ?? null, options.limit ?? 20]);
      const cached = searches.get(key);
      const fetchIt = async (signal?: AbortSignal) => {
        const page = await client.get(`${base}/places/search`, {
          query: { q: query || undefined, types: options.types?.join(','), country: options.country, within: options.within, limit: options.limit ?? 20, lang: scope.lang },
          schema: geoSearchSchema,
          signal,
        });
        return remember(page.items.map(toPlaceOption));
      };
      // An empty query is the viewer's recent places, which change with every pick: never served from the cache.
      if (cached && query.trim()) {
        if (Date.now() - cached.at > FRESH_MS) void fetchIt().then((items) => searches.set(key, { at: Date.now(), items })).catch(() => undefined);
        return cached.items;
      }
      const items = await fetchIt(options.signal);
      if (query.trim()) searches.set(key, { at: Date.now(), items });
      return items;
    },
    async get(id: string, signal?: AbortSignal) {
      if (places.has(id)) return places.get(id) ?? null;
      const inFlight = pending.get(id);
      if (inFlight) return awaitUnlessAborted(inFlight, signal);
      const request = client
        .get(`${base}/places/${encodeURIComponent(id)}`, { query: { lang: scope.lang }, schema: geoPlaceSchema })
        .then((p) => {
          const option = toPlaceOption(p);
          places.set(id, option);
          return option;
        })
        .catch((error: unknown) => {
          if ((error as { status?: number })?.status === 404) {
            places.set(id, null);
            return null;
          }
          throw error;
        })
        .finally(() => pending.delete(id));
      pending.set(id, request);
      return awaitUnlessAborted(request, signal);
    },
    async children(id: string, options: { types?: string[]; signal?: AbortSignal } = {}) {
      const list = await client.get(`${base}/places/${encodeURIComponent(id)}/children`, { query: { types: options.types?.join(','), lang: scope.lang, limit: 200 }, schema: z.array(geoPlaceSchema), signal: options.signal });
      return remember(list.map(toPlaceOption));
    },
    async resolve(text: string, options: { country?: string; signal?: AbortSignal } = {}) {
      const result = await client.post(`${base}/places/resolve`, { text, country: options.country ?? null }, { query: { lang: scope.lang }, schema: geoResolveSchema, signal: options.signal });
      return { place: result.place ? toPlaceOption(result.place) : null, chain: remember(result.chain.map(toPlaceOption)), unmatched: result.unmatched };
    },
    used(id: string) {
      void client.post(`${base}/places/${encodeURIComponent(id)}/used`, {}).catch(() => undefined);
    },
    peek(id: string) {
      return places.get(id) ?? undefined;
    },
    invalidate() {
      searches.clear();
      places.clear();
    },
    dispose() {
      liveSources.delete(source);
    },
  };
  liveSources.add(source);
  return source;
}

/** The user's own display formats (Preferences › Language and region), on top of the resolved chain (D-032). */
export const userPlaceFormatKeys: Record<PlaceFormatContext, string> = {
  short: 'geo.displayFormat.short',
  long: 'geo.displayFormat.long',
  picker: 'geo.displayFormat.picker',
  document: 'geo.displayFormat.document',
};

export function toCountryAddressSchema(s: GeoCountrySchema): CountryAddressSchema {
  return { countryCode: s.countryCode, levels: s.levels, required: s.required, postalPattern: s.postalPattern, templates: s.templates };
}
